import axios from 'axios';

const MAILGUN_API_KEY = '0cedf3716395b2c2646ba65024920067-3d4b3a2a-c8fbd56f';
const MAILGUN_DOMAIN = 'globalcompactsa.org.za';
const MAILGUN_API_URL = `https://api.eu.mailgun.net/v3/${MAILGUN_DOMAIN}`;

class EmailService {
    constructor() {
        this.auth = {
            username: 'api',
            password: MAILGUN_API_KEY
        };
    }

    async sendEmail({ to, subject, html, text, from = 'Global Compact SA <sme@globalcompactsa.org.za>' }) {
        try {
            const formData = new FormData();
            formData.append('from', from);
            formData.append('to', to);
            formData.append('subject', subject);
            formData.append('html', html);
            formData.append('text', text);

            console.log('Attempting to send email:', { to, subject, from });
            
            const response = await axios.post(`${MAILGUN_API_URL}/messages`, formData, {
                auth: this.auth,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log('Email sent successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error sending email:', {
                to,
                subject,
                error: error.response?.data || error.message,
                status: error.response?.status
            });
            throw error;
        }
    }

    async sendBulkEmails({ recipients, subject, html, text, from = 'Global Compact SA <sme@globalcompactsa.org.za>' }) {
        try {
            const formData = new FormData();
            formData.append('from', from);
            formData.append('to', recipients.join(','));
            formData.append('subject', subject);
            formData.append('html', html);
            formData.append('text', text);

            const response = await axios.post(`${MAILGUN_API_URL}/messages`, formData, {
                auth: this.auth,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error sending bulk emails:', error);
            throw error;
        }
    }

    async sendTemplateEmail({ to, template, data, from = 'Global Compact SA <sme@globalcompactsa.org.za>' }) {
        try {
            const { subject, html, text } = this.getTemplateContent(template, data);
            return await this.sendEmail({ to, subject, html, text, from });
        } catch (error) {
            console.error('Error sending template email:', error);
            throw error;
        }
    }

    getEmailTemplate(content, data) {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Global Compact SA</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        color: #333;
                        margin: 0;
                        padding: 0;
                    }
                    .container {
                        max-width: 600px;
                        margin: 0 auto;
                        padding: 20px;
                    }
                    .header {
                        text-align: center;
                        padding: 20px 0;
                        background-color: #f8f9fa;
                        border-bottom: 1px solid #e9ecef;
                    }
                    .logo {
                        max-width: 200px;
                        height: auto;
                        margin-bottom: 20px;
                    }
                    .content {
                        padding: 30px 0;
                    }
                    .footer {
                        text-align: center;
                        padding: 20px;
                        background-color: #f8f9fa;
                        border-top: 1px solid #e9ecef;
                        font-size: 12px;
                        color: #6c757d;
                    }
                    .social-links {
                        margin: 15px 0;
                    }
                    .social-links a {
                        color: #6c757d;
                        text-decoration: none;
                        margin: 0 10px;
                    }
                    .contact-info {
                        margin: 10px 0;
                    }
                    .contact-info p {
                        margin: 5px 0;
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <div class="header">
                        <img src="https://globalcompactsa.org.za/wp-content/uploads/2023/03/GCNSALogo.png" alt="Global Compact SA Logo" class="logo">
                    </div>
                    <div class="content">
                        ${content}
                    </div>
                    <div class="footer">
                        <div class="social-links">
                            <a href="https://www.linkedin.com/company/global-compact-network-south-africa/">LinkedIn</a> |
                            <a href="https://twitter.com/GCNSouthAfrica">Twitter</a>
                        </div>
                        <div class="contact-info">
                            <p>Email: info@globalcompactsa.org.za</p>
                            <p>Phone: +27 (0) 11 784 1111</p>
                            <p>Address: 1 Jan Smuts Avenue, Braamfontein, Johannesburg, 2000</p>
                        </div>
                        <p>© ${new Date().getFullYear()} Global Compact Network South Africa. All rights reserved.</p>
                    </div>
                </div>
            </body>
            </html>
        `;
    }

    getTemplateContent(template, data) {
        const templates = {
            ACCOUNT_CREATED: {
                subject: 'Welcome to Global Compact SA',
                html: this.getEmailTemplate(`
                    <h2 style="color: #5e5873; margin-bottom: 20px;">Welcome to Global Compact SA!</h2>
                    <p>Dear ${data.username},</p>
                    <p>Your account has been created successfully. We're excited to have you on board!</p>
                    <p>You can now log in to your account and start using our platform.</p>
                    <p>Best regards,<br>Global Compact SA Team</p>
                `, data),
                text: `
                    Welcome to Global Compact SA!
                    Dear ${data.username},
                    Your account has been created successfully. We're excited to have you on board!
                    You can now log in to your account and start using our platform.
                    Best regards,
                    Global Compact SA Team
                `
            },
            PASSWORD_CHANGED: {
                subject: 'Password Changed Successfully',
                html: this.getEmailTemplate(`
                    <h2 style="color: #5e5873; margin-bottom: 20px;">Password Changed Successfully</h2>
                    <p>Dear ${data.username},</p>
                    <p>Your password has been changed successfully.</p>
                    <p>If you did not make this change, please contact our support team immediately.</p>
                    <p>Best regards,<br>Global Compact SA Team</p>
                `, data),
                text: `
                    Password Changed Successfully
                    Dear ${data.username},
                    Your password has been changed successfully.
                    If you did not make this change, please contact our support team immediately.
                    Best regards,
                    Global Compact SA Team
                `
            },
            // Add more templates as needed
        };

        return templates[template] || {
            subject: 'Notification from Global Compact SA',
            html: this.getEmailTemplate(`
                <h2 style="color: #5e5873; margin-bottom: 20px;">Notification from Global Compact SA</h2>
                <p>Dear ${data.username},</p>
                <p>This is a notification from Global Compact SA.</p>
                <p>Best regards,<br>Global Compact SA Team</p>
            `, data),
            text: 'This is a notification from Global Compact SA.'
        };
    }
}

export const emailService = new EmailService(); 